import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SubscriptionProvider, useSubscription } from "@/context/SubscriptionContext";
import { UpgradePrompt } from "@/components/UpgradePrompt";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import ActivityGenerator from "@/pages/ActivityGenerator";
import ActivityView from "@/pages/ActivityView";
import MakeBilingual from "@/pages/MakeBilingual";
import MyActivities from "@/pages/MyActivities";
import AskBrilla from "@/pages/AskBrilla";
import ClassroomProfile from "@/pages/ClassroomProfile";
import WeeklyPlanner from "@/pages/WeeklyPlanner";
import MyWeeklyPlans from "@/pages/MyWeeklyPlans";
import WeeklyPlanView from "@/pages/WeeklyPlanView";
import LearningCenters from "@/pages/LearningCenters";
import FamilyConnection from "@/pages/FamilyConnection";
import BookCompanion from "@/pages/BookCompanion";
import CoachDashboard from "@/pages/CoachDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Pricing from "@/pages/Pricing";
import ResourceLibrary from "@/pages/ResourceLibrary";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-10 w-10 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function roleHome(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/admin";
  if (user.role === "coach") return "/coach";
  return user.onboarding_completed ? "/dashboard" : "/onboarding";
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={roleHome(user)} replace />;
  return children;
}

function ProRoute({ children, feature }) {
  const { hasPro, loading } = useSubscription();
  if (loading) return <Spinner />;
  if (!hasPro) return <UpgradePrompt feature={feature} />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><ActivityGenerator /></ProtectedRoute>} />
      <Route path="/make-bilingual" element={<ProtectedRoute><ProRoute feature="Make My Activity Bilingual"><MakeBilingual /></ProRoute></ProtectedRoute>} />
      <Route path="/learning-centers" element={<ProtectedRoute><LearningCenters /></ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute><FamilyConnection /></ProtectedRoute>} />
      <Route path="/book-companion" element={<ProtectedRoute><BookCompanion /></ProtectedRoute>} />
      <Route path="/weekly-planner" element={<ProtectedRoute><ProRoute feature="The Weekly Planner"><WeeklyPlanner /></ProRoute></ProtectedRoute>} />
      <Route path="/weekly-plans" element={<ProtectedRoute><ProRoute feature="Weekly Plans"><MyWeeklyPlans /></ProRoute></ProtectedRoute>} />
      <Route path="/weekly-plans/:id" element={<ProtectedRoute><ProRoute feature="Weekly Plans"><WeeklyPlanView /></ProRoute></ProtectedRoute>} />
      <Route path="/activities" element={<ProtectedRoute><MyActivities /></ProtectedRoute>} />
      <Route path="/activities/:id" element={<ProtectedRoute><ActivityView /></ProtectedRoute>} />
      <Route path="/ask-brilla" element={<ProtectedRoute><ProRoute feature="Ask Brilla"><AskBrilla /></ProRoute></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ClassroomProfile /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute><ResourceLibrary /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
      <Route path="/coach" element={<ProtectedRoute roles={["coach", "admin"]}><CoachDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </SubscriptionProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
